// src/hooks/useRealtimeCodeEditor.js
/**
 * Code Editor Data Hook with HTTP API
 * ====================================
 * Manages projects and files state using HTTP API
 * Handles optimistic updates for instant UI feedback
 */

import { useState, useEffect, useCallback } from 'react';
import { codeEditorApi } from '../utils/api/CodeEditorApi';

export const useRealtimeCodeEditor = (classContext, options = {}) => {
  const { enabled = true } = options;

  // Local state
  const [projects, setProjects] = useState([]);
  const [standaloneFiles, setStandaloneFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Optimistic updates tracking
  const [pendingUpdates, setPendingUpdates] = useState(new Set());

  // Load data from API
  const loadData = useCallback(async () => {
    if (!classContext?.class_id) {
      return;
    }

    const startTime = performance.now();
    console.log('[useRealtimeCodeEditor] Starting to load projects and files...');
    setLoading(true);
    setError(null);

    try {
      const response = await codeEditorApi.getUserProjects(classContext);
      const loadTime = performance.now() - startTime;

      if (response.success) {
        setProjects(response.data.projects || []);
        setStandaloneFiles(response.data.standalone_files || []);
        console.log(`[useRealtimeCodeEditor] Loaded ${response.data.projects?.length || 0} projects and ${response.data.standalone_files?.length || 0} files in ${loadTime.toFixed(2)}ms`);
      } else {
        console.error(`[useRealtimeCodeEditor] Failed to load projects after ${loadTime.toFixed(2)}ms:`, response.error);
        setError('Failed to load projects');
      }
    } catch (err) {
      const loadTime = performance.now() - startTime;
      console.error(`[useRealtimeCodeEditor] Error loading projects after ${loadTime.toFixed(2)}ms:`, err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [classContext]);

  // Load data when classContext changes
  useEffect(() => {
    if (enabled && classContext?.class_id) {
      loadData();
    }
  }, [enabled, classContext?.class_id, loadData]);

  // Optimistic update: Create Project
  const createProjectOptimistic = useCallback(async (projectName, runtimeLanguage, description = '') => {
    const updateKey = `create_project_${projectName}`;
    setPendingUpdates(prev => new Set([...prev, updateKey]));

    // Add temporary project immediately
    const tempProject = {
      project_id: `temp_${Date.now()}`,
      name: projectName,
      runtime_language: runtimeLanguage,
      description,
      files: [],
      created_at: new Date().toISOString(),
      _optimistic: true
    };
    setProjects(prev => [...prev, tempProject]);

    try {
      const response = await codeEditorApi.createProject(projectName, runtimeLanguage, description, classContext);

      if (response.success) {
        // Replace temp with real project
        setProjects(prev => prev.map(p =>
          p.project_id === tempProject.project_id ? { ...response.project, files: [] } : p
        ));
      } else {
        // Remove temp on failure
        setProjects(prev => prev.filter(p => p.project_id !== tempProject.project_id));
        throw new Error(response.error || 'Failed to create project');
      }

      return response;
    } catch (error) {
      setProjects(prev => prev.filter(p => p.project_id !== tempProject.project_id));
      throw error;
    } finally {
      setPendingUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [classContext]);

  // Optimistic update: Create File
  const createFileOptimistic = useCallback(async (filename, content = '', projectId = null) => {
    const updateKey = `create_file_${filename}`;
    setPendingUpdates(prev => new Set([...prev, updateKey]));

    // Add temporary file immediately
    const tempFile = {
      file: {
        file_id: `temp_${Date.now()}`,
        name: filename,
        path: '/',
        syntax_language: 'javascript'
      },
      content: {
        current: content,
        size_bytes: content.length,
        line_count: content.split('\n').length
      },
      project_id: projectId,
      created_at: new Date().toISOString(),
      _optimistic: true
    };

    if (projectId) {
      setProjects(prev => prev.map(project =>
        project.project_id === projectId
          ? { ...project, files: [...(project.files || []), tempFile] }
          : project
      ));
    } else {
      setStandaloneFiles(prev => [...prev, tempFile]);
    }

    try {
      const response = await codeEditorApi.createFile(filename, content, projectId, classContext);

      if (response.success) {
        // Replace temp with real file
        if (projectId) {
          setProjects(prev => prev.map(project =>
            project.project_id === projectId
              ? {
                  ...project,
                  files: (project.files || []).map(f =>
                    f.file.file_id === tempFile.file.file_id ? response.file : f
                  )
                }
              : project
          ));
        } else {
          setStandaloneFiles(prev => prev.map(f =>
            f.file.file_id === tempFile.file.file_id ? response.file : f
          ));
        }
      } else {
        // Remove temp on failure
        if (projectId) {
          setProjects(prev => prev.map(project =>
            project.project_id === projectId
              ? { ...project, files: (project.files || []).filter(f => f.file.file_id !== tempFile.file.file_id) }
              : project
          ));
        } else {
          setStandaloneFiles(prev => prev.filter(f => f.file.file_id !== tempFile.file.file_id));
        }
        throw new Error(response.error || 'Failed to create file');
      }

      return response;
    } catch (error) {
      // Remove temp on error
      if (projectId) {
        setProjects(prev => prev.map(project =>
          project.project_id === projectId
            ? { ...project, files: (project.files || []).filter(f => f.file.file_id !== tempFile.file.file_id) }
            : project
        ));
      } else {
        setStandaloneFiles(prev => prev.filter(f => f.file.file_id !== tempFile.file.file_id));
      }
      throw error;
    } finally {
      setPendingUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [classContext]);

  // Optimistic update: Update File
  const updateFileOptimistic = useCallback(async (fileId, content, changeType = 'manual_edit') => {
    const updateKey = `update_file_${fileId}`;
    setPendingUpdates(prev => new Set([...prev, updateKey]));

    // Update content immediately
    const updateFileContent = (file) => {
      if (file.file.file_id === fileId) {
        return {
          ...file,
          content: {
            ...file.content,
            current: content,
            size_bytes: content.length,
            line_count: content.split('\n').length
          },
          updated_at: new Date().toISOString(),
          _optimistic: true
        };
      }
      return file;
    };

    setProjects(prev => prev.map(project => ({
      ...project,
      files: (project.files || []).map(updateFileContent)
    })));
    setStandaloneFiles(prev => prev.map(updateFileContent));

    try {
      const response = await codeEditorApi.updateFile(fileId, content, changeType, classContext);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update file');
      }

      return response;
    } catch (error) {
      // Refresh from server on error
      loadData();
      throw error;
    } finally {
      setPendingUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [classContext, loadData]);

  return {
    // State
    projects,
    standaloneFiles,
    loading,
    error,

    // Actions with optimistic updates
    createProject: createProjectOptimistic,
    createFile: createFileOptimistic,
    updateFile: updateFileOptimistic,

    // Utility
    refresh: loadData,
    pendingUpdates: Array.from(pendingUpdates),
    hasPendingUpdates: pendingUpdates.size > 0
  };
};
